import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import { userApi } from '../api'
import { captureEvent } from '../posthog'
import { useTranslation } from '../i18n'
import WelcomeTourModal, { useWelcomeSlideCount } from './WelcomeTourModal'
import CoachmarkOverlay from './CoachmarkOverlay'
import { listTours, getTour } from './tours/registry'
import {
  isTourComplete,
  type TourDefinition,
  type TourId,
} from './tours/types'
import type { TourIconId } from './tourVisuals'

type Phase = 'idle' | 'welcome' | 'coachmarks'

type TourBucketCopy = {
  skip?: string
  next?: string
  done?: string
  steps?: Record<string, string>
}

interface TourContextValue {
  phase: Phase
  activeTourId: TourId | null
  /** Start a registered tour if incomplete (or force). Returns false if blocked/unknown. */
  startTour: (tourId: TourId, opts?: { force?: boolean }) => boolean
  isTourComplete: (tourId: TourId) => boolean
}

const TourContext = createContext<TourContextValue>({
  phase: 'idle',
  activeTourId: null,
  startTour: () => false,
  isTourComplete: () => true,
})

// Separate file recommended for hooks to avoid fast-refresh issues
// eslint-disable-next-line react-refresh/only-export-components
export function useTour() {
  return useContext(TourContext)
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth()
  const { preferences, loading: prefsLoading, refreshPreferences } = useUserPreferences()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const [phase, setPhase] = useState<Phase>('idle')
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null)
  const [welcomeIndex, setWelcomeIndex] = useState(0)
  const [stepIndex, setStepIndex] = useState(0)
  const [finishing, setFinishing] = useState(false)
  const [sessionProgress, setSessionProgress] = useState<Record<string, number>>({})

  const welcomeSlideCount = useWelcomeSlideCount(
    activeTour?.hasWelcome ? activeTour.copyKey : undefined
  )

  const progressPrefs = useMemo(
    () => ({
      ...preferences,
      tmaTourProgress: {
        ...preferences?.tmaTourProgress,
        ...sessionProgress,
      },
    }),
    [preferences, sessionProgress]
  )

  const isComplete = useCallback(
    (tourId: TourId) => {
      const tour = getTour(tourId)
      if (!tour) return true
      return isTourComplete(progressPrefs, tour)
    },
    [progressPrefs]
  )

  const persistDone = useCallback(
    async (tour: TourDefinition) => {
      if (finishing) return
      setSessionProgress(prev => ({ ...prev, [tour.id]: tour.version }))
      setFinishing(true)
      setPhase('idle')
      setActiveTour(null)
      try {
        await userApi.updateUserPreferences({
          tmaTourProgress: { [tour.id]: tour.version },
        })
        captureEvent('tour_completed', { tour_id: tour.id, tour_version: tour.version })
        await refreshPreferences()
      } catch (err) {
        console.error('[Tour] Failed to persist tour progress', err)
      } finally {
        setFinishing(false)
      }
    },
    [finishing, refreshPreferences]
  )

  const beginTour = useCallback((tour: TourDefinition) => {
    setActiveTour(tour)
    setWelcomeIndex(0)
    setStepIndex(0)
    if (tour.hasWelcome) {
      setPhase('welcome')
      return
    }
    if (tour.steps.length === 0) {
      void persistDone(tour)
      return
    }
    setPhase('coachmarks')
  }, [persistDone])

  const startTour = useCallback(
    (tourId: TourId, opts?: { force?: boolean }) => {
      const tour = getTour(tourId)
      if (!tour) {
        console.warn('[Tour] Unknown tour id:', tourId)
        return false
      }
      if (phase !== 'idle' || finishing) return false
      if (!opts?.force && isTourComplete(progressPrefs, tour)) return false
      beginTour(tour)
      const first = tour.hasWelcome ? undefined : tour.steps[0]
      if (first?.path && location.pathname !== first.path) {
        navigate(first.path)
      }
      return true
    },
    [phase, finishing, progressPrefs, beginTour, location.pathname, navigate]
  )

  // Re-evaluate whenever user/prefs change so traineeWithCoach can start after a coach is assigned.
  useEffect(() => {
    if (authLoading || prefsLoading || finishing || !user || phase !== 'idle') return
    for (const tour of listTours()) {
      if (!tour.autoStart) continue
      if (isTourComplete(progressPrefs, tour)) continue
      if (!tour.autoStart({ user, preferences })) continue
      beginTour(tour)
      return
    }
  }, [authLoading, prefsLoading, finishing, user, preferences, progressPrefs, phase, beginTour])

  const goToCoachmark = useCallback(
    (index: number) => {
      if (!activeTour) return
      let i = index
      while (i < activeTour.steps.length) {
        const step = activeTour.steps[i]
        const present = document.querySelector(`[data-tour="${step.target}"]`)
        if (!step.optional || present) {
          setStepIndex(i)
          setPhase('coachmarks')
          if (step.path && location.pathname !== step.path) {
            navigate(step.path)
          }
          return
        }
        i += 1
      }
      void persistDone(activeTour)
    },
    [activeTour, location.pathname, navigate, persistDone]
  )

  const handleWelcomeNext = useCallback(() => {
    if (welcomeIndex < welcomeSlideCount - 1) {
      setWelcomeIndex(i => i + 1)
      return
    }
    goToCoachmark(0)
  }, [welcomeIndex, welcomeSlideCount, goToCoachmark])

  const handleCoachmarkNext = useCallback(() => {
    if (!activeTour) return
    if (stepIndex < activeTour.steps.length - 1) {
      goToCoachmark(stepIndex + 1)
      return
    }
    void persistDone(activeTour)
  }, [activeTour, stepIndex, goToCoachmark, persistDone])

  useEffect(() => {
    if (phase !== 'coachmarks' || !activeTour) return
    const step = activeTour.steps[stepIndex]
    if (step?.path && location.pathname !== step.path) {
      navigate(step.path)
    }
  }, [phase, stepIndex, location.pathname, navigate, activeTour])

  const value = useMemo(
    () => ({
      phase,
      activeTourId: activeTour?.id ?? null,
      startTour,
      isTourComplete: isComplete,
    }),
    [phase, activeTour?.id, startTour, isComplete]
  )

  const step = activeTour?.steps[stepIndex]
  const tourCopyBucket = activeTour
    ? (t.tour as Record<string, TourBucketCopy>)[activeTour.copyKey]
    : undefined
  const stepsCopy = tourCopyBucket?.steps ?? {}
  const stepTitle =
    step && step.titleKey in stepsCopy ? stepsCopy[step.titleKey] : step?.titleKey ?? ''
  const stepBody =
    step && step.bodyKey in stepsCopy ? stepsCopy[step.bodyKey] : step?.bodyKey ?? ''
  const stepIcon: TourIconId = step?.iconId ?? 'quickAccess'

  return (
    <TourContext.Provider value={value}>
      {children}
      {phase === 'welcome' && activeTour?.hasWelcome && (
        <WelcomeTourModal
          copyKey={activeTour.copyKey}
          slideIndex={welcomeIndex}
          slideCount={welcomeSlideCount}
          onNext={handleWelcomeNext}
          onSkip={() => activeTour && void persistDone(activeTour)}
        />
      )}
      {phase === 'coachmarks' && activeTour && step && (
        <CoachmarkOverlay
          target={step.target}
          title={stepTitle}
          body={stepBody}
          stepIndex={stepIndex}
          stepCount={activeTour.steps.length}
          iconId={stepIcon}
          skipLabel={tourCopyBucket?.skip ?? 'Skip'}
          nextLabel={tourCopyBucket?.next ?? 'Next'}
          doneLabel={tourCopyBucket?.done ?? 'Done'}
          tipPlacement={step.tipPlacement}
          optional={step.optional}
          onNext={handleCoachmarkNext}
          onSkip={() => void persistDone(activeTour)}
        />
      )}
    </TourContext.Provider>
  )
}
